// Shared constants between Convex and client code

import { toTreeOrder } from "./todoReducer";

export const MAX_ROUTINES_PER_USER = 20;
export const MAX_ROUTINE_NAME_LENGTH = 80;
export const MAX_ROUTINE_TASKS = 40;
export const MAX_ROUTINE_TASK_LENGTH = 300;

// Cross-device chat read frontier: the server keeps only the most recent writes
// per user and session (newest last) and never orders them; readers pick the
// furthest id they can locate in their own message list.
export const CHAT_FRONTIER_MAX_IDS = 8;
export const CHAT_FRONTIER_MAX_ID_LENGTH = 512;

export function stripFrogMarkers(text: string): string {
  return text.replaceAll("🐸", "").replace(/\s{2,}/g, " ").trim();
}

// The shape stored in sessionParticipants.todos and synced to clients.
export type SessionTodo = {
  id: string;
  text: string;
  completed: boolean;
  parentId?: string;
};

/** Validate rows and drop duplicate ids, preserving the array's order and parent links. */
export function validateFlatRows(value: unknown): SessionTodo[] {
  if (!Array.isArray(value)) return [];
  const rows: SessionTodo[] = [];
  const seen = new Set<string>();
  for (const candidate of value) {
    if (typeof candidate !== "object" || candidate === null || Array.isArray(candidate)) continue;
    const { id, text, completed, parentId } = candidate;
    if (
      typeof id !== "string" ||
      !id ||
      seen.has(id) ||
      typeof text !== "string" ||
      typeof completed !== "boolean"
    ) {
      continue;
    }
    seen.add(id);
    rows.push({
      id,
      text,
      completed,
      ...(typeof parentId === "string" && parentId ? { parentId } : {}),
    });
  }
  return rows;
}

/** Valid rows in tree order: roots in array order, each followed by its children. */
export function sanitizeSessionTodos(value: unknown): SessionTodo[] {
  return toTreeOrder(validateFlatRows(value));
}

export type PastSessionTodos = {
  sessionId: string;
  sessionStartTime: number;
  todos: SessionTodo[];
};

export const PARTICIPANT_PROPERTY_FIELDS: {
  todos: "todos";
  segmentAdjustments: "segmentAdjustments";
} = {
  todos: "todos",
  segmentAdjustments: "segmentAdjustments",
};

export type ParticipantPropertyName = keyof typeof PARTICIPANT_PROPERTY_FIELDS;

type ParticipantJoinHistory = {
  participantKey?: string;
  participantKeys?: readonly string[];
};

export function hasParticipantJoinedSession(participant?: ParticipantJoinHistory | null): boolean {
  return Boolean(participant?.participantKey || participant?.participantKeys?.length);
}

export const MAX_SESSION_PARTICIPANTS = 15;

export function hasSessionParticipantCapacity(participantCount: number) {
  return participantCount < MAX_SESSION_PARTICIPANTS;
}

export function participantPropertyValuesEqual(left: unknown, right: unknown) {
  const normalizedLeft = left ?? null;
  const normalizedRight = right ?? null;
  if (normalizedLeft === normalizedRight) return true;

  try {
    return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
  } catch {
    return false;
  }
}

export const VIDEO_PROVIDERS: {
  jaas: "jaas";
  selfHosted: "self_hosted";
  selfHostedExperimental: "self_hosted_experimental";
} = {
  jaas: "jaas",
  selfHosted: "self_hosted",
  // Same Jitsi stack and JWT key as self_hosted, but on the video-test domain,
  // where our Rust videobridge (wt-bridge) replaces JVB.
  selfHostedExperimental: "self_hosted_experimental",
};

export type VideoProvider = (typeof VIDEO_PROVIDERS)[keyof typeof VIDEO_PROVIDERS];

export type SelfHostedVideoProvider =
  | typeof VIDEO_PROVIDERS.selfHosted
  | typeof VIDEO_PROVIDERS.selfHostedExperimental;

/** Both self-hosted providers share every behaviour except the Jitsi domain. */
export function isSelfHostedProvider(provider: VideoProvider): provider is SelfHostedVideoProvider {
  return (
    provider === VIDEO_PROVIDERS.selfHosted || provider === VIDEO_PROVIDERS.selfHostedExperimental
  );
}

// ── Background effect (blur / curated virtual backgrounds) ────────────────
// Curated image allowlist. The Convex validator in schema.ts and the picker's
// thumbnail grid are both derived from this list; each id must have
// public/background-effect/backgrounds/<id>.webp (+ <id>.thumb.webp) shipped
// BEFORE it appears here — a missing asset fails closed (camera off + notice).
export const BACKGROUND_EFFECT_IMAGE_IDS = ["horizon", "linen", "terrace", "meridian"] as const;

export type BackgroundEffectImageId = (typeof BACKGROUND_EFFECT_IMAGE_IDS)[number];

export function isBackgroundEffectImageId(value: string): value is BackgroundEffectImageId {
  return (BACKGROUND_EFFECT_IMAGE_IDS as readonly string[]).includes(value);
}

// Persisted background-effect preference (users.backgroundEffect).
// Missing field = { kind: "none" }.
export type BackgroundEffectPreference =
  | { kind: "none" }
  | { kind: "blur" }
  | { kind: "image"; imageId: BackgroundEffectImageId };

export const DEFAULT_BACKGROUND_EFFECT: BackgroundEffectPreference = { kind: "none" };

// Host notification channel preference. The Convex validator in schema.ts is
// derived from this list.
export const NOTIFICATION_MODES = ["sound", "notify", "both"] as const;

export type NotificationMode = (typeof NOTIFICATION_MODES)[number];

export const DEFAULT_NOTIFICATION_MODE: NotificationMode = "sound";

// ── Session deletion ───────────────────────────────────────────────────────
// A host can cancel a session until people could plausibly be joining it
// (the join window opens 60 minutes before start), with a short grace period
// after creation so accidentally created sessions can always be removed.
// If no one else has booked, the host can cancel at any time — there's no one
// relying on the session, so the join-window cutoff doesn't apply.

export const SESSION_DELETE_CUTOFF_MS = 60 * 60 * 1000;
export const SESSION_DELETE_GRACE_MS = 5 * 60 * 1000;

export function isSessionDeletable(
  startTime: number,
  createdAt: number,
  now: number,
  hasOtherParticipants: boolean,
): boolean {
  return (
    !hasOtherParticipants ||
    now < startTime - SESSION_DELETE_CUTOFF_MS ||
    now < createdAt + SESSION_DELETE_GRACE_MS
  );
}

export const SESSION_DELETE_ERRORS: {
  windowClosed: string;
  notHost: string;
} = {
  windowClosed:
    "This session can no longer be cancelled — it starts within the hour.",
  notHost: "Only the host can cancel a session.",
};

export const SESSION_UNBOOK_ERRORS: {
  alreadyStarted: string;
  isHost: string;
} = {
  alreadyStarted: "This session has already started, so you can't unbook.",
  isHost: "Hosts can't unbook — cancel the session instead.",
};

export type SessionCreateErrorCode = "SESSION_CREATE_TOO_FAR_PAST";

export type SessionCreateError = {
  code: SessionCreateErrorCode;
  message: string;
};

export const SESSION_CREATE_ERRORS: {
  tooFarPast: SessionCreateError;
} = {
  tooFarPast: {
    code: "SESSION_CREATE_TOO_FAR_PAST",
    message: "Sessions cannot be created more than 2 hours in the past.",
  },
};
